import { CloseOutlined, ExclamationCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Cascader, Col, Form, message, Modal, Row, Select, Space, Tooltip, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, Grid, Integration, IntegrationColumn, IntegrationMapping } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import './integration-mappings-page.scoped.scss';

function ExistingMapping(props: { mapping?: IntegrationMapping; onSaved?: () => void }) {
  const [loading, setLoading] = useState(false);

  const deleteMapping = () => {
    setLoading(true);
    apiClient
      .delete(`mappings/${props.mapping?.id}`)
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDeleteClick = () => {
    Modal.confirm({
      title: 'Eşleştirme sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Eşleştirmeyi silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: deleteMapping,
    });
  };

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <Space>
        <span>
          {props.mapping?.field.grid.name} / {props.mapping?.field.name}
        </span>
        {props.mapping?.joinField && (
          <span>
            (Eşleştirme: {props.mapping?.joinField.grid.name} / {props.mapping?.joinField.name})
          </span>
        )}
        <Button size="small" shape="circle" icon={<CloseOutlined />} onClick={onDeleteClick} loading={loading} />
      </Space>
    </div>
  );
}

function NewMapping(props: { integrationId?: string; columnName?: string; grids?: Grid[]; onSaved?: () => void }) {
  const [form] = useForm();

  const [selectedFieldId, setSelectedFieldId] = useState<string>();
  const [selectedJoinFieldId, setSelectedJoinFieldId] = useState<string>();
  const [selectedForeignKeyGrid, setSelectedForeignKeyGrid] = useState<Grid>();
  const [foreignKeyJoinFieldOptions, setForeignKeyJoinFieldOptions] = useState<Field[]>();
  const [loading, setLoading] = useState(false);

  const options = props.grids?.map(g => {
    return {
      value: g.id,
      label: g.name,
      children: g.fields
        ?.filter(f => !['SUBGRID', 'DYNAMIC', 'RULEBASED', 'LOOKUP', 'AUTOINC', 'FILE_UPLOAD', 'PARENTROW'].includes(f.dataType))
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.map(f => {
          return {
            value: f.id,
            label: f.name,
          };
        }),
    };
  });

  const fields = props.grids?.flatMap(g => g.fields);

  function onFieldChange(value: any) {
    if (value.length < 3) {
      setSelectedFieldId(undefined);
    }
    const fieldId = value[1];
    setSelectedJoinFieldId(undefined);
    setForeignKeyJoinFieldOptions(undefined);
    setSelectedForeignKeyGrid(undefined);
    setSelectedFieldId(fieldId);
    form.setFieldsValue({ joinField: undefined });
    const field = fields?.find(f => f.id === fieldId);
    const foreignKeyField = fields?.find(f => f.id === field?.foreignKeyFieldId);
    let foreignKeyGrid: Grid | undefined = undefined;
    if (foreignKeyField && props.grids?.length) {
      for (let grid of props.grids) {
        for (let field of grid.fields) {
          if (field.id === foreignKeyField.id) {
            foreignKeyGrid = grid;
            break;
          }
        }
      }
    }
    setSelectedForeignKeyGrid(foreignKeyGrid);
    setForeignKeyJoinFieldOptions(foreignKeyGrid?.fields);
  }

  function onJoinFieldChange(value: string) {
    setSelectedJoinFieldId(value);
  }

  function onSaveClick() {
    if (!selectedFieldId || (foreignKeyJoinFieldOptions && !selectedJoinFieldId) || loading) {
      return;
    }
    const body = {
      integrationId: props.integrationId,
      columnName: props.columnName,
      fieldId: selectedFieldId,
      joinFieldId: selectedJoinFieldId,
    };
    setLoading(true);
    apiClient
      .post('mappings', body)
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
        form.resetFields();
        setSelectedFieldId(undefined);
        setSelectedJoinFieldId(undefined);
        setSelectedForeignKeyGrid(undefined);
        setForeignKeyJoinFieldOptions(undefined);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Form form={form}>
      <Row gutter={16}>
        <Col span="11">
          <Form.Item name="field" noStyle>
            <Cascader options={options} onChange={onFieldChange} style={{ width: '100%' }} placeholder="Veri elemanı seçin" />
          </Form.Item>
        </Col>
        {foreignKeyJoinFieldOptions && (
          <Col span="11">
            <Form.Item name="joinField" noStyle>
              <Select onChange={onJoinFieldChange} style={{ width: '100%' }} placeholder="Eşleştirme veri elemanı seçin">
                {foreignKeyJoinFieldOptions?.map(o => (
                  <Select.Option key={o.id} value={o.id}>
                    {selectedForeignKeyGrid?.name + ' / ' + o.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        <Col span="2">
          <Button
            type="default"
            disabled={!selectedFieldId || (foreignKeyJoinFieldOptions && !selectedJoinFieldId)}
            loading={loading}
            onClick={onSaveClick}
          >
            Kaydet
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

function RequiredValidationMessage(props: { gridName?: string }) {
  const { Text } = Typography;
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <Space>
        <Text type="danger">
          <span>{props.gridName} veri setinden bir veri elemanı ile eşleştirilmelidir.</span>
        </Text>
        <Tooltip
          title={`${props.gridName} veri seti veri elemanlarıyla eşleştirilmiş entegrasyon veri elemanları mevcut. Entegrasyon verilerinin veri setlerine aktarılabilmesi için tanım veri elemanlarının eşleştirilmesi gerekmektedir; aksi durumda entegrasyon ile alınan verilerin veri setlerine aktarılması mümkün olmayacaktır.`}
        >
          <Text type="secondary">
            <QuestionCircleOutlined />
          </Text>
        </Tooltip>
      </Space>
    </div>
  );
}

function MappingCell(props: {
  column?: IntegrationColumn;
  integration?: Integration;
  grids?: Grid[];
  mappedGrids?: Grid[];
  columnMappings?: (IntegrationMapping & { key: string })[];
  onSaved?: () => void;
}) {
  return (
    <td width="70%">
      {!!props.columnMappings?.length && (
        <div style={{ marginBottom: '1.5rem' }}>
          {props.columnMappings
            .filter(m => m.columnName === props.column?.name)
            .map(m => (
              <ExistingMapping key={m.key} mapping={m} onSaved={props.onSaved} />
            ))}
        </div>
      )}
      <NewMapping integrationId={props.integration?.id} columnName={props.column?.name} grids={props.grids} onSaved={props.onSaved} />
      {props.column?.isIdentifier &&
        props.mappedGrids &&
        props.mappedGrids?.map(mg => {
          if (props.columnMappings && props.columnMappings.some(cm => cm.field.grid.id === mg.id)) {
            return null;
          }
          return <RequiredValidationMessage gridName={mg.name} />;
        })}
    </td>
  );
}

function ExistingNestedMapping(props: { mapping?: IntegrationMapping; grids?: Grid[]; onSaved?: () => void }) {
  const { Text } = Typography;
  const [loading, setLoading] = useState(false);

  const deleteMapping = () => {
    setLoading(true);
    apiClient
      .delete(`mappings/${props.mapping?.id}`)
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDeleteClick = () => {
    Modal.confirm({
      title: 'Eşleştirme sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Eşleştirmeyi silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: deleteMapping,
    });
  };

  // Find the subgrid to get field names
  const parentGrid = props.grids?.find(g => g.fields?.some(f => f.id === props.mapping?.field.id));
  const subGrid = parentGrid?.subGrids?.find(sg => sg.id === props.mapping?.field.subGridId);

  return (
    <div style={{ marginBottom: '2rem', padding: '0.75rem', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          <Text>
            {props.mapping?.field.grid.name} / {props.mapping?.field.name}
          </Text>
          <Button size="small" shape="circle" icon={<CloseOutlined />} onClick={onDeleteClick} loading={loading} />
        </Space>
        {props.mapping?.subGridMappings && props.mapping.subGridMappings.length > 0 && (
          <table style={{ width: '100%', fontSize: '12px', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafafa' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #d9d9d9' }}>Kaynak veri</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #d9d9d9' }}>Alt veri seti</th>
              </tr>
            </thead>
            <tbody>
              {props.mapping.subGridMappings.map((sgm, index) => {
                const field = subGrid?.fields?.find(f => f.id === sgm.fieldId);
                return (
                  <tr key={index}>
                    <td style={{ padding: '4px 8px', border: '1px solid #d9d9d9' }}>{sgm.columnName.split('.').pop()}</td>
                    <td style={{ padding: '4px 8px', border: '1px solid #d9d9d9' }}>{field?.name || sgm.fieldId}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Space>
    </div>
  );
}

function NewNestedMapping(props: {
  integrationId?: string;
  columnName?: string;
  subColumns?: IntegrationColumn[];
  grids?: Grid[];
  onSaved?: () => void;
}) {
  const { Text } = Typography;
  const [selectedSubGridFieldId, setSelectedSubGridFieldId] = useState<string>();
  const [subGrid, setSubGrid] = useState<Grid>();
  const [subColumnMappings, setSubColumnMappings] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [cascaderValue, setCascaderValue] = useState<any[]>();

  // Get cascader options with only grids that have SUBGRID type fields
  const getSubGridCascaderOptions = () => {
    const options: { value: string; label: string; children: { value: string; label: string }[] }[] = [];

    props.grids?.forEach(grid => {
      const subGridFields = grid.fields?.filter(field => field.dataType === 'SUBGRID');

      if (subGridFields && subGridFields.length > 0) {
        options.push({
          value: grid.id,
          label: grid.name,
          children: subGridFields.map(field => ({
            value: field.id,
            label: field.name,
          })),
        });
      }
    });

    return options;
  };

  const subGridCascaderOptions = getSubGridCascaderOptions();

  const onSubGridFieldChange = (value: any) => {
    setCascaderValue(value);

    if (!value || value.length < 2) {
      setSelectedSubGridFieldId(undefined);
      setSubGrid(undefined);
      setSubColumnMappings({});
      return;
    }

    const fieldId = value[1];
    setSelectedSubGridFieldId(fieldId);
    setSubColumnMappings({});

    // Find the selected SUBGRID field
    const allFields = props.grids?.flatMap(g => g.fields || []);

    const selectedField = allFields?.find(f => f.id === fieldId);

    if (selectedField?.subGridId) {
      // Find the parent grid that contains this field
      const parentGrid = props.grids?.find(g => g.fields?.some(f => f.id === selectedField.id));

      // Get the subgrid from the parent grid's subGrids property
      const foundSubGrid = parentGrid?.subGrids?.find(sg => sg.id === selectedField.subGridId);

      if (foundSubGrid) {
        setSubGrid(foundSubGrid);
      } else {
        setSubGrid(undefined);
      }
    } else {
      setSubGrid(undefined);
    }
  };

  const onSubColumnFieldChange = (subColumnName: string, fieldId: string) => {
    setSubColumnMappings(prev => ({
      ...prev,
      [subColumnName]: fieldId,
    }));
  };

  const onSaveClick = () => {
    if (!selectedSubGridFieldId || loading || !subGrid) {
      return;
    }

    // Check if subgrid has identifier fields
    const identifierFields = subGrid.fields?.filter(f => f.isIdentifier);

    if (identifierFields && identifierFields.length > 0) {
      // Check if all identifier fields are mapped
      const unmappedIdentifierFields: string[] = [];

      identifierFields.forEach(idField => {
        // Check if this identifier field is mapped in subColumnMappings
        const isMapped = Object.values(subColumnMappings).includes(idField.id);
        if (!isMapped) {
          unmappedIdentifierFields.push(idField.name);
        }
      });

      if (unmappedIdentifierFields.length > 0) {
        message.error(`Belirteç veri elemanları eşleştirilmelidir: ${unmappedIdentifierFields.join(', ')}`);
        return;
      }
    }

    // Convert subColumnMappings object to array format for API
    const subGridMappingsArray = Object.entries(subColumnMappings).map(([columnName, fieldId]) => ({
      columnName,
      fieldId,
    }));

    const body = {
      integrationId: props.integrationId,
      columnName: props.columnName,
      fieldId: selectedSubGridFieldId,
      subGridMappings: subGridMappingsArray,
    };

    setLoading(true);
    apiClient
      .post('mappings', body)
      .then(() => {
        message.success('Eşleştirmeler kaydedildi');
        // Reset form to blank state
        setSelectedSubGridFieldId(undefined);
        setSubGrid(undefined);
        setSubColumnMappings({});
        setCascaderValue(undefined);
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const canSave = selectedSubGridFieldId && Object.keys(subColumnMappings).length > 0;

  return (
    <>
      <Row gutter={16}>
        <Col span={16}>
          <Cascader
            style={{ width: '100%' }}
            placeholder="Alt veri seti seçin"
            options={subGridCascaderOptions}
            onChange={onSubGridFieldChange}
            value={cascaderValue}
          />
        </Col>
        <Col span={2}>
          <Button type="default" disabled={!canSave} loading={loading} onClick={onSaveClick}>
            Kaydet
          </Button>
        </Col>
      </Row>

      {selectedSubGridFieldId && subGrid && (
        <div style={{ marginTop: '1rem' }}>
          {props.subColumns?.map(sc => (
            <Row key={sc.name} gutter={16} style={{ marginBottom: '0.5rem' }}>
              <Col span={6}>
                <Text>{sc.name.split('.').pop()}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Veri elemanı seçin"
                  value={subColumnMappings[sc.name]}
                  onChange={value => onSubColumnFieldChange(sc.name, value as string)}
                >
                  {subGrid.fields?.map(field => (
                    <Select.Option key={field.id} value={field.id}>
                      {field.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          ))}
        </div>
      )}
    </>
  );
}

function NestedMappingCell(props: {
  column?: IntegrationColumn;
  subColumns?: IntegrationColumn[];
  integration?: Integration;
  grids?: Grid[];
  mappedGrids?: Grid[];
  columnMappings?: (IntegrationMapping & { key: string })[];
  onSaved?: () => void;
}) {
  // Filter nested mappings (those with subGridMappings)
  const nestedMappings = props.columnMappings?.filter(m => m.subGridMappings && m.subGridMappings.length > 0);

  return (
    <td width="70%">
      {!!nestedMappings?.length && (
        <div style={{ marginBottom: '1.5rem' }}>
          {nestedMappings.map(m => (
            <ExistingNestedMapping key={m.key} mapping={m} grids={props.grids} onSaved={props.onSaved} />
          ))}
        </div>
      )}

      <NewNestedMapping
        integrationId={props.integration?.id}
        columnName={props.column?.name}
        subColumns={props.subColumns}
        grids={props.grids}
        onSaved={props.onSaved}
      />
    </td>
  );
}

export function IntegrationMappingsPage(props: { integration?: Integration }) {
  const { Text } = Typography;

  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columns, setColumns] = useState<IntegrationColumn[]>();
  const [gridsLoading, setGridsLoading] = useState(false);
  const [grids, setGrids] = useState<(Grid & { key: string })[]>();
  const [mappings, setMappings] = useState<(IntegrationMapping & { key: string })[]>();
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappedGrids, setMappedGrids] = useState<Grid[]>();

  useEffect(() => {
    if (!props.integration) {
      return;
    }

    let cacheColumns: IntegrationColumn[] = [];
    if (props.integration.sourceColumns?.length) {
      cacheColumns = props.integration.sourceColumns.map(c => {
        const mapping = new IntegrationColumn();
        mapping.name = c;
        mapping.isIdentifier = props.integration?.identifierColumns?.some(i => i === c) || false;
        return mapping;
      });

      cacheColumns = [...cacheColumns.filter(c => c.isIdentifier), ...cacheColumns.filter(c => !c.isIdentifier)];
      setColumns(cacheColumns);
    }

    setColumnsLoading(true);
    apiClient
      .get(`integrations/${props.integration.id}/columns`)
      .then(response => {
        if (response.data) {
          setColumns(response.data);
        }
      })
      .catch(() => {
        if (!cacheColumns) {
          message.error('Kaynak veriye ulaşılamadı. Lütfen bağlantı bilgilerini kontrol edin.');
        }
      })
      .finally(() => {
        setColumnsLoading(false);
      });
  }, [props.integration]);

  useEffect(() => {
    setGridsLoading(true);
    apiClient
      .get('grids')
      .then(response => {
        const responseGrids: Grid[] = response.data;
        const responseData = responseGrids.map(grid => {
          return {
            key: grid.id,
            ...grid,
          };
        });
        setGrids(responseData);
        setGridsLoading(false);
      })
      .catch((reason: any) => {
        setGridsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  const loadMappings = useCallback(() => {
    setMappingsLoading(true);
    apiClient
      .get(`mappings?integrationid=${props.integration?.id}`)
      .then(response => {
        const responseMappings: IntegrationMapping[] = response.data;
        const responseData = responseMappings.map(mapping => {
          return {
            key: mapping.id,
            ...mapping,
          };
        });
        setMappings(responseData);

        const distinctGrids = [] as Grid[];
        responseMappings.forEach(m => {
          if (!distinctGrids.some(g => g.id === m.field.grid.id)) {
            distinctGrids.push(m.field.grid);
          }
        });
        setMappedGrids(distinctGrids);

        setMappingsLoading(false);
      })
      .catch((reason: any) => {
        setMappingsLoading(false);
        apiErrorMessage(reason);
      });
  }, [props.integration?.id]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  if (!columns || !grids || !mappings) {
    if (columnsLoading || gridsLoading || mappingsLoading) {
      return <p>Yükleniyor, lütfen bekleyin.</p>;
    }
    return null;
  }

  // Filter to get only top-level columns (no dots in the name)
  const topLevelColumns = columns.filter(c => !c.name.includes('.'));

  // Helper function to get subcolumns for a parent column
  const getSubColumns = (parentColumnName: string): IntegrationColumn[] => {
    return columns
      .filter(c => c.name.startsWith(parentColumnName + '.'))
      .map(c => ({ name: c.name.split('.')[1], isIdentifier: c.isIdentifier }));
  };

  // Helper function to check if a column has subcolumns
  const hasSubColumns = (columnName: string): boolean => {
    return columns.some(c => c !== columns.find(col => col.name === columnName) && c.name.startsWith(columnName + '.'));
  };

  return (
    <table style={{ width: '100%' }}>
      <tbody>
        {topLevelColumns?.map((c, i) => {
          const hasSubCols = hasSubColumns(c.name);
          const subColumns = hasSubCols ? getSubColumns(c.name) : undefined;

          return (
            <tr key={c.name} className="mapping-row">
              <td width="5%" style={{ textAlign: 'center' }}>
                {i + 1}
              </td>
              <td width="25%">
                <Space>
                  {c.isIdentifier && <Text type="danger">*</Text>}
                  <Text strong={c.isIdentifier}>{c.name}</Text>
                </Space>
              </td>
              {hasSubCols ? (
                <NestedMappingCell
                  key={c.name}
                  column={c}
                  subColumns={subColumns}
                  integration={props.integration}
                  grids={grids}
                  columnMappings={mappings?.filter(m => m.columnName === c.name)}
                  mappedGrids={mappedGrids}
                  onSaved={loadMappings}
                />
              ) : (
                <MappingCell
                  key={c.name}
                  column={c}
                  integration={props.integration}
                  grids={grids}
                  columnMappings={mappings?.filter(m => m.columnName === c.name)}
                  mappedGrids={mappedGrids}
                  onSaved={loadMappings}
                />
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
